import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProductServices } from "../../../services/products";
import { Icon } from "../../Icon";
import "./SeeProducts.scss";
import Label from "../../Label";
import BackToTop from "../../common/BackToTop";
import { useAsync } from "../../../utils/hooks/useAsync";
import Loading from "../../common/Loading";
import Paginator from "../../Paginator";
import ExcelJS from "exceljs";

type Filter = "all" | "gold" | "silver";

type ProductsOrder = {
  sku: null | 1 | -1;
  title: null | 1 | -1;
  material: null | 1 | -1;
  department: null | 1 | -1;
};

const SeeProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [displayProducts, setDisplayProducts] = useState<Product[]>([]);
  const [currentFilter, setCurrentFilter] = useState<Filter>("all");
  const { isSuccess, isLoading, run } = useAsync();
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchField, setSearchField] = useState<string>("");
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const productsOrderInit: ProductsOrder = {
    sku: null,
    title: null,
    material: null,
    department: null,
  };
  const [productsOrder, setProductsOrder] =
    useState<ProductsOrder>(productsOrderInit);
  useEffect(() => {
    let order =
      productsOrder.sku != null
        ? "sku:" + productsOrder.sku
        : productsOrder.department != null
        ? "department:" + productsOrder.department
        : productsOrder.material != null
        ? "material:" + productsOrder.material
        : productsOrder.title != null
        ? "title:" + productsOrder.title
        : null;
    run(
      ProductServices.getProductsAdmin({
        search: searchValue,
        material: currentFilter,
        order: order != null ? order : "",
        limit: 20,
        skip: (selectedPage - 1) * 20,
      })
    ).then((data: any) => {
      const totalProducts = data.size;
      setPages(Math.ceil(totalProducts / 20));
      setProducts(data.data);
    });
  }, [, selectedPage, productsOrder, currentFilter, searchValue]);

  useEffect(() => {
    setDisplayProducts(
      [...products].map((x) => {
        return {
          ...x,
          department:
            x.department === "brazalete"
              ? "Pulsera"
              : x.department === "chain"
              ? "Cadena"
              : x.department === "ring"
              ? "Anillo"
              : x.department === "pendant"
              ? "Dije"
              : x.department === "hoop"
              ? "Aro"
              : x.department === "finding"
              ? "Fornitura"
              : x.department,
          material:
            x.material === "gold"
              ? "Oro"
              : x.material === "silver"
              ? "Plata"
              : x.material,
        };
      })
    );
  }, [products]);

  const navigate = useNavigate();

  const handleSearch = (e: any) => {
    setSearchValue(searchField);
    e.currentTarget.value.toLowerCase();
    setSearchField("");
  };

  const getColumnLetter = (columnIndex: number) => {
    let dividend = columnIndex;
    let columnName = "";

    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }

    return columnName;
  };

  const handleDownloadCatalog = async () => {
    try {
      setIsDownloading(true);
      const response = await ProductServices.getProductsSpreadsheet();
      const products = response?.data || [];
      const hechuras = response?.hechuras || [];

      const workbook = new ExcelJS.Workbook();
      workbook.calcProperties.fullCalcOnLoad = true;
      const productsSheet = workbook.addWorksheet("Productos");
      const hechurasSheet = workbook.addWorksheet("Hechuras");

      const productColumns = [
        { header: "ProductId", key: "ProductId", width: 28 },
        { header: "Titulo", key: "Titulo", width: 40 },
        { header: "Codigo producto", key: "Codigo producto", width: 20 },
        { header: "Material", key: "Material", width: 14 },
        { header: "Categoria", key: "Categoria", width: 26 },
        { header: "Tags", key: "Tags", width: 20 },
        { header: "Color", key: "Color", width: 16 },
        { header: "Hilo/Espesor", key: "Hilo/Espesor", width: 16 },
        { header: "Largo/Medidas", key: "Largo/Medidas", width: 18 },
        { header: "Peso", key: "Peso", width: 12 },
        { header: "Hechura", key: "Hechura", width: 48 },
        { header: "HechuraId", key: "HechuraId", width: 28 },
      ];

      productsSheet.columns = productColumns;
      productsSheet.addRows(products);
      productsSheet.getRow(1).font = { bold: true };

      const hechuraColumns = [
        { header: "Hechura", key: "Hechura", width: 48 },
        { header: "HechuraId", key: "HechuraId", width: 28 },
        { header: "title", key: "title", width: 20 },
        { header: "description", key: "description", width: 28 },
        { header: "value", key: "value", width: 12 },
        { header: "material", key: "material", width: 14 },
      ];

      hechurasSheet.columns = hechuraColumns;
      hechurasSheet.addRows(hechuras);
      hechurasSheet.getRow(1).font = { bold: true };

      if (productsSheet.rowCount > 1 && hechurasSheet.rowCount > 1) {
        const hechuraColumnIndex =
          productColumns.findIndex((col) => col.key === "Hechura") + 1;
        const hechuraIdColumnIndex =
          productColumns.findIndex((col) => col.key === "HechuraId") + 1;
        const hechurasRangeEndRow = Math.max(2, hechurasSheet.rowCount);
        const hechuraListRange = `Hechuras!$A$2:$A$${hechurasRangeEndRow}`;
        const hechuraLookupRange = `Hechuras!$A$2:$B$${hechurasRangeEndRow}`;
        const productsEndRow = Math.max(2, productsSheet.rowCount);
        const hechuraColumnLetter = getColumnLetter(hechuraColumnIndex);

        for (let rowIndex = 2; rowIndex <= productsEndRow; rowIndex += 1) {
          const hechuraCell = productsSheet
            .getRow(rowIndex)
            .getCell(hechuraColumnIndex);
          const hechuraIdCell = productsSheet
            .getRow(rowIndex)
            .getCell(hechuraIdColumnIndex);
          const formula = `IFERROR(VLOOKUP(${hechuraColumnLetter}${rowIndex},${hechuraLookupRange},2,FALSE),"")`;

          hechuraCell.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [`=${hechuraListRange}`],
          };
          hechuraIdCell.value = {
            formula,
          };
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `catalogo-productos.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error descargando catalogo", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOrder = (item: "sku" | "title" | "material" | "department") => {
    if (productsOrder[item] === -1 || productsOrder[item] === null) {
      setSelectedPage(1);
      setProductsOrder({ ...productsOrderInit, [item]: 1 });
    } else {
      setSelectedPage(1);
      setProductsOrder({ ...productsOrderInit, [item]: -1 });
    }
  };

  const filters = [
    {
      title: "Todos",
      value: "all" as Filter,
      onClick: () => {
        setDisplayProducts(
          products.map((x) => {
            return {
              ...x,
              department:
                x.department === "brazalete"
                  ? "Pulsera"
                  : x.department === "chain"
                  ? "Cadena"
                  : x.department === "ring"
                  ? "Anillo"
                  : x.department === "pendant"
                  ? "Dije"
                  : x.department === "hoop"
                  ? "Aro"
                  : x.department === "finding"
                  ? "Fornitura"
                  : x.department,
              material:
                x.material === "gold"
                  ? "Oro"
                  : x.material === "silver"
                  ? "Plata"
                  : x.material,
            };
          })
        );
        setCurrentFilter("all");
      },
    },
    {
      title: "Oro",
      value: "gold" as Filter,
      onClick: () => {
        setCurrentFilter("gold");
      },
    },
    {
      title: "Plata",
      value: "silver" as Filter,
      onClick: () => {
        setCurrentFilter("silver");
      },
    },
  ];

  const handleSelectPage = (page: number) => {
    setSelectedPage(page);
  };

  const handleChange = (e: any) => {
    setSearchField(e.currentTarget.value);
  };
  const handleEnter = (e: any) => {
    e.key == "Enter" && handleSearch(e);
  };

  return (
    <>
      <div className="seeProductsContainer">
        <div className="header">
          <div>
            <Label tag="h3">Productos</Label>
          </div>
          <div className="headerButtons">
            <div className="headerButtons__input">
              <Icon
                iconName="search_admin"
                className="headerButtons__input--icon"
              />
              <input
                className="addProduct"
                style={{ backgroundColor: "white", color: "black" }}
                type="text"
                placeholder="Buscar"
                value={searchField}
                name="product"
                id="product"
                onChange={handleChange}
                onKeyDown={handleEnter}
              />
            </div>
            <button
              onClick={() => navigate("/admin/add")}
              className="addProduct__button"
            >
              <p className="addProduct__icon">+</p> Nuevo
            </button>
            <button
              onClick={handleDownloadCatalog}
              className="addProduct__button"
              disabled={isDownloading}
            >
              {isDownloading ? "Descargando..." : "Descargar catalogo"}
            </button>
          </div>
        </div>
        <div className="selectButtons">
          {filters.map((filter, index) => (
            <button
              key={index}
              className={`select-button ${
                currentFilter == filter.value && "select-button__actived"
              }`}
              onClick={filter.onClick}
            >
              {filter.title}
            </button>
          ))}
        </div>

        <div className="itemHeader">
          <div className=" itemRow itemRow__sku">
            <Label tag="p" variants="body2" className="itemRow__label">
              Código
            </Label>
            <div onClick={() => handleOrder("sku")} className="itemRow__icon">
              <Icon
                iconName={
                  productsOrder.sku === -1 || productsOrder.sku === null
                    ? "down_arrow"
                    : "up_arrow"
                }
                className="itemRow__icon"
              />
            </div>
          </div>
          <div className="itemRow itemRow__title">
            <Label tag="p" variants="body2" className="itemRow__label">
              Nombre
            </Label>
            <div onClick={() => handleOrder("title")} className="itemRow__icon">
              <Icon
                iconName={
                  productsOrder.title === -1 || productsOrder.title === null
                    ? "down_arrow"
                    : "up_arrow"
                }
                className="itemRow__icon"
              />
            </div>
          </div>
          <div className="itemRow itemRow__material">
            <Label tag="p" variants="body2" className="itemRow__label">
              Material
            </Label>
            <div
              onClick={() => handleOrder("material")}
              className="itemRow__icon"
            >
              <Icon
                iconName={
                  productsOrder.material === -1 ||
                  productsOrder.material === null
                    ? "down_arrow"
                    : "up_arrow"
                }
                className="itemRow__icon"
              />
            </div>
          </div>
          <div className="itemRow itemRow__department">
            <Label tag="p" variants="body2" className="itemRow__label">
              Categoría
            </Label>
            <div
              onClick={() => handleOrder("department")}
              className="itemRow__icon"
            >
              <Icon
                iconName={
                  productsOrder.department === -1 ||
                  productsOrder.department === null
                    ? "down_arrow"
                    : "up_arrow"
                }
                className="itemRow__icon"
              />
            </div>
          </div>
        </div>
        {displayProducts &&
          isSuccess &&
          displayProducts.map((prod) => {
            return (
              <Link key={prod._id} to={"/admin/edit/" + prod.friendlyUrl}>
                <div className="itemRow">
                  <Label tag="p" variants="body1" className="itemRow__sku">
                    {prod.sku}
                  </Label>
                  <Label tag="p" variants="body1" className="itemRow__title">
                    {prod.title}
                  </Label>
                  <Label
                    tag="p"
                    variants="body1"
                    className="itemRow__department"
                  >
                    {prod.material}
                  </Label>
                  <Label
                    tag="p"
                    variants="body1"
                    className="itemRow__department"
                  >
                    {prod.department}
                  </Label>
                  <div className="itemRow__icon-container">
                    <Icon iconName="edit_icon_admin" />
                  </div>
                </div>
              </Link>
            );
          })}
        {isLoading && <Loading />}
        {isSuccess && products && products.length > 0 && (
          <div className="paginator">
            <Paginator
              pages={pages}
              handleSelectPage={handleSelectPage}
              selectedPage={selectedPage}
            />
          </div>
        )}
        <BackToTop />
      </div>
    </>
  );
};

export default SeeProducts;
